import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { LanguageService } from './commons/services/language.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { createTranslateLoader, initializeApp } from './translate.config';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { AuthService } from './auth/auth.service';
import { UiUtilsService } from './commons/services/UiUtils.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { AuthInterceptorService } from './commons/interceptors/auth-interceptor.service';
import { HttpErrorInterceptor } from './commons/interceptors/http-error-interceptor.service';
import { ACCESSIBLE_COMPANIES_POLL_INTERVAL, APPLICATION_NAME, REFRESH_INTERVAL, TOKEN_BUFFER_TIME } from '@socium/sociumflow';
import { Applications } from '@socium/common-api';
import {
  APP_URLS_CONFIG,
  ENV_CONFIG_TOKEN,
  EnvConfig,
  SociumApp,
  provideAppStates,
} from '@socium/sociumflow';
import { environment } from '../environments/environment';

const ENV_CONFIG: EnvConfig = {
  environment: environment.env,
  appName: SociumApp.JOB,
  apiUrl: environment.workSpaceEndpoint,
  intercomAppId: environment.intercomAppId,
  showIntercom: environment.showIntercom,
};

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
    secondary: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    importProvidersFrom(
      BrowserAnimationsModule,
      BrowserModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(TranslateService),
        inject(LanguageService),
      );
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    AuthService,
    UiUtilsService,
    MessageService,
    ConfirmationService,
    DialogService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    { provide: ENV_CONFIG_TOKEN, useValue: ENV_CONFIG },
    {
      provide: APP_URLS_CONFIG,
      useValue: {
        WORKSPACE: environment.workspaceUrl,
        JOB: environment.url,
        WORKFLOW: environment.workflowUrl,
        PERF: environment.perfUrl,
        DOC: environment.sociumDocUrl,
        PAYROLL: environment.payrollUrl,
        TIME: environment.timeUrl,
        WORKSPACE_API: environment.workSpaceEndpoint,
        ENV: environment.env,
      },
    },
    { provide: APPLICATION_NAME, useValue: Applications.JOB },
    { provide: REFRESH_INTERVAL, useValue: environment.refreshInterval },
    { provide: TOKEN_BUFFER_TIME, useValue: environment.tokenBufferTime },
    {
      provide: ACCESSIBLE_COMPANIES_POLL_INTERVAL,
      useValue: environment.accessibleCompaniesPollInterval,
    },
    ...provideAppStates(),
  ],
};
